/**
 * Browser-to-S3 upload. **Bytes never pass through the API** — the Lambda's
 * 6 MB synchronous request payload makes anything else impossible (§3b), which
 * is why the server issues credentials and the browser moves the file.
 *
 * Two paths, chosen by size against the server's own threshold:
 *
 *  - **below it, a presigned POST.** POST and not PUT, because
 *    `content-length-range` is a POST policy condition and cannot be expressed
 *    on a signed PUT — a PUT-based size cap is unenforced while looking
 *    enforced;
 *  - **at or above it, S3 multipart** in 16 MB parts, with each part's `ETag`
 *    collected from the response. That header must be *exposed* by the bucket
 *    CORS configuration or `xhr.getResponseHeader('ETag')` returns null and the
 *    completion call has nothing to send.
 *
 * Part URLs get a 6-hour TTL, and an expired one is **re-requested rather than
 * restarting** — a 1 GB upload on a slow link outlives a short expiry
 * mid-transfer (§6.13 [R4]).
 */

import { api } from '@/lib/api';

export interface UploadTarget {
  entityType: string;
  entityId: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

interface PresignedPost {
  kind: 'presigned_post';
  attachmentId: string;
  url: string;
  fields: Record<string, string>;
}

interface MultipartStart {
  kind: 'multipart';
  attachmentId: string;
  uploadId: string;
  partSizeBytes: number;
  partCount: number;
  partUrls: { partNumber: number; url: string }[];
}

/** One `XMLHttpRequest`, because `fetch` still cannot report upload progress. */
function send(
  method: 'POST' | 'PUT',
  url: string,
  body: XMLHttpRequestBodyInit,
  onProgress?: (loaded: number) => void,
): Promise<{ status: number; etag: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded);
    });
    xhr.addEventListener('load', () =>
      resolve({ status: xhr.status, etag: xhr.getResponseHeader('ETag') }),
    );
    xhr.addEventListener('error', () => reject(new Error('The upload could not reach S3.')));
    xhr.addEventListener('abort', () => reject(new Error('The upload was cancelled.')));
    xhr.send(body);
  });
}

export interface UploadOptions {
  file: File;
  target: UploadTarget;
  /** §8 item 7 — required before the presign call AND before multipart initiation. */
  exportControlledDataWillBeEntered: boolean;
  multipartThresholdBytes: number;
  onProgress?: (progress: UploadProgress) => void;
}

export async function uploadAttachment(options: UploadOptions): Promise<string> {
  const { file, target, multipartThresholdBytes, onProgress } = options;

  const requestBody = {
    entityType: target.entityType,
    entityId: target.entityId,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    exportControlledDataWillBeEntered: options.exportControlledDataWillBeEntered,
  };

  if (file.size < multipartThresholdBytes) {
    const presigned = await api<PresignedPost>('/api/attachments/presign', {
      method: 'POST',
      body: requestBody,
    });

    const form = new FormData();
    for (const [key, value] of Object.entries(presigned.fields)) form.append(key, value);
    // The file is appended LAST: S3 ignores every field after the file part.
    form.append('file', file);

    const result = await send('POST', presigned.url, form, (loaded) =>
      onProgress?.({ loaded, total: file.size }),
    );
    if (result.status < 200 || result.status >= 300) {
      throw new Error(`S3 refused the upload (${result.status}).`);
    }

    await api(`/api/attachments/${presigned.attachmentId}/confirm`, { method: 'POST' });
    return presigned.attachmentId;
  }

  const start = await api<MultipartStart>('/api/attachments/multipart', {
    method: 'POST',
    body: requestBody,
  });

  const urls = new Map(start.partUrls.map((part) => [part.partNumber, part.url]));
  const parts: { partNumber: number; etag: string }[] = [];
  let uploaded = 0;

  for (let partNumber = 1; partNumber <= start.partCount; partNumber += 1) {
    let url = urls.get(partNumber);
    if (url === undefined) {
      // Issued in batches, and re-requested when an upload outlives them.
      const more = await api<{ partUrls: { partNumber: number; url: string }[] }>(
        '/api/attachments/multipart/parts',
        {
          method: 'POST',
          body: {
            attachmentId: start.attachmentId,
            partNumbers: Array.from(
              { length: Math.min(50, start.partCount - partNumber + 1) },
              (_unused, index) => partNumber + index,
            ),
          },
        },
      );
      for (const part of more.partUrls) urls.set(part.partNumber, part.url);
      url = urls.get(partNumber);
    }
    if (url === undefined) throw new Error(`No upload URL for part ${partNumber}.`);

    const offset = (partNumber - 1) * start.partSizeBytes;
    const chunk = file.slice(offset, offset + start.partSizeBytes);
    const base = uploaded;
    const result = await send('PUT', url, chunk, (loaded) =>
      onProgress?.({ loaded: base + loaded, total: file.size }),
    );
    if (result.status < 200 || result.status >= 300) {
      throw new Error(`S3 refused part ${partNumber} (${result.status}).`);
    }
    if (result.etag === null) {
      throw new Error(
        'S3 did not expose an ETag for this part. The bucket CORS configuration must expose it.',
      );
    }
    parts.push({ partNumber, etag: result.etag });
    uploaded += chunk.size;
  }

  await api(`/api/attachments/multipart/${start.attachmentId}/complete`, {
    method: 'POST',
    body: { parts },
  });

  return start.attachmentId;
}
